import React, { useEffect, useRef, useState } from "react";

const imageNames = [
  "awake",
  "jare2",
  "kaki1",
  "kaki2",
  "mati2",
  "mati3",
  "sleep1",
  "sleep2",
  // directions
  "right1",
  "right2",
  "left1",
  "left2",
  "up1",
  "up2",
  "down1",
  "down2",
  "upright1",
  "upright2",
  "upleft1",
  "upleft2",
  "dwright1",
  "dwright2",
  "dwleft1",
  "dwleft2",
];
const nekoSize = 32;
const images = Object.fromEntries(
  imageNames.map((name) => {
    const image = new Image(nekoSize, nekoSize);
    // public/bitmaps is served at /bitmaps
    image.src = "/bitmaps/" + name + ".png";
    return [name, image];
  }),
);

const stateMachine: Record<string, any> = {
  sleep: {
    image: ["sleep1", "sleep2"],
    imageInterval: 1,
    click: "awake",
  },
  awake: {
    image: "awake",
    nextState: "normal",
    nextStateDelay: 2.5,
  },
  normal: {
    image: "mati2",
    nextState: ["normal", "normal", "normal", "tilt", "scratch", "yawn"],
    nextStateDelay: 1.5,
  },
  tilt: {
    image: "jare2",
    nextState: "normal",
    nextStateDelay: 1,
  },
  yawn: {
    image: "mati3",
    nextState: ["normal", "normal", "sleep"],
    nextStateDelay: 1,
  },
  scratch: {
    image: ["kaki1", "kaki2"],
    imageInterval: 0.1,
    nextState: "normal",
    nextStateDelay: 3,
  },
};

export default function Neko({
  enabled,
  sleepX,
  sleepY,
  // setSleepPos,
  setEnabled,
}: {
  enabled: boolean;
  sleepX: number | null;
  sleepY: number | null;
  // setSleepPos: (x: number, y: number) => void;
  setEnabled: (v: boolean) => void;
}) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [state, setStateInternal] = useState<string>("sleep");
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [isReturning, setIsReturning] = useState<boolean>(false);
  const animationIndex = useRef(0);
  const nextStateTimeout = useRef<number | null>(null);
  const imageCycleInterval = useRef<number | null>(null);
  const following = useRef(false);
  const rafRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const targetPos = useRef<{ x: number; y: number } | null>(null);
  const pos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // init position when enabled
  useEffect(() => {
    if (!enabled) return;
    // default pos: 10px from left, and offset 50px from bottom
    const defaultX = 10;
    const defaultY = (window.innerHeight || 400) - nekoSize - 50;

    pos.current.x = sleepX ?? defaultX;
    pos.current.y = sleepY ?? defaultY;
    if (wrapperRef.current) {
      wrapperRef.current.style.left = pos.current.x + "px";
      wrapperRef.current.style.top = pos.current.y + "px";
    }
  }, [enabled, sleepX, sleepY]);

  // state machine in react
  useEffect(() => {
    if (!enabled) return;

    function clearTimers() {
      if (nextStateTimeout.current) {
        window.clearTimeout(nextStateTimeout.current);
        nextStateTimeout.current = null;
      }
      if (imageCycleInterval.current) {
        window.clearInterval(imageCycleInterval.current);
        imageCycleInterval.current = null;
      }
    }

    function renderImage(stName = state) {
      let name: any = stateMachine[stName].image;
      animationIndex.current++;
      if (Array.isArray(name)) {
        name = name[animationIndex.current % name.length];
      }
      if (imgRef.current && images[name]) imgRef.current.src = images[name].src;
    }

    function setStateName(stateNameOrArray: any) {
      clearTimers();
      let stateName = stateNameOrArray;
      if (Array.isArray(stateNameOrArray)) {
        stateName = stateNameOrArray[Math.floor(Math.random() * stateNameOrArray.length)];
      }
      if (!stateMachine[stateName]) {
        console.warn("Unknown state", stateName);
        return;
      }
      setStateInternal(stateName);
      const stateData = stateMachine[stateName];

      if (stateData.nextState) {
        nextStateTimeout.current = window.setTimeout(
          () => setStateName(stateData.nextState),
          (stateData.nextStateDelay ?? 1) * 1000,
        );
      }

      if (stateData.imageInterval) {
        imageCycleInterval.current = window.setInterval(
          () => renderImage(stateName),
          (stateData.imageInterval ?? 1) * 1000,
        );
      }
      renderImage(stateName);
    }

    setStateName(state);

    return () => {
      clearTimers();
    };
  }, [enabled, state]);

  // options roughly matching the go implementation
  const DefaultOptions = {
    step: 6, // pixels per tick (reduced for slower movement)
    dmax: 20, // proximity threshold
    runTicks: 2, // ticks per run frame
    alertTicks: 2,
  };

  // movement & animation options reference
  const options = DefaultOptions;

  // map vector to one of the image direction prefixes
  function directionName(fromX: number, fromY: number, toX: number, toY: number) {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const α = Math.atan2(dy, dx); // -pi..pi where 0 is to the right
    // map to 8 directions
    const deg = α * (180 / Math.PI);
    if (deg >= -22.5 && deg <= 22.5) return "right";
    if (deg > 22.5 && deg <= 67.5) return "downright";
    if (deg > 67.5 && deg <= 112.5) return "down";
    if (deg > 112.5 && deg <= 157.5) return "downleft";
    if (deg > 157.5 || deg <= -157.5) return "left";
    if (deg > -157.5 && deg <= -112.5) return "upleft";
    if (deg > -112.5 && deg <= -67.5) return "up";
    return "upright";
  }

  // mousemove listener only while actively following (not while returning home)
  useEffect(() => {
    if (!enabled) return;
    if (!isFollowing) return;

    function onMove(e: MouseEvent) {
      targetPos.current = { x: e.clientX - nekoSize / 2, y: e.clientY - nekoSize / 2 };
    }

    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [enabled, isFollowing]);

  // run loop: performs discrete steps towards `targetPos` and animates frames
  useEffect(() => {
    if (!enabled) return;
    if (!following.current && !isReturning) return;
    let runTick = 0;
    let even = false;

    function performStep() {
      if ((!following.current && !isReturning) || !wrapperRef.current || !targetPos.current)
        return;

      // when following or returning, cancel any state-machine timers so images don't fight
      if (nextStateTimeout.current) {
        window.clearTimeout(nextStateTimeout.current);
        nextStateTimeout.current = null;
      }
      if (imageCycleInterval.current) {
        window.clearInterval(imageCycleInterval.current);
        imageCycleInterval.current = null;
      }

      const t = targetPos.current!;
      const p = pos.current;
      const dx = t.x - p.x;
      const dy = t.y - p.y;
      const dist = Math.hypot(dx, dy);

      if (dist > options.dmax) {
        // run towards cursor using fixed step magnitude
        const dstep = options.step / dist;
        p.x += dstep * dx;
        p.y += dstep * dy;
        wrapperRef.current!.style.left = Math.round(p.x) + "px";
        wrapperRef.current!.style.top = Math.round(p.y) + "px";

        // animate run frames based on direction
        const dir = directionName(p.x, p.y, t.x, t.y);
        runTick++;
        if (runTick >= options.runTicks) {
          runTick = 0;
          even = !even;
        }
        const frame = even ? "2" : "1";
        const base = dir === "downright" ? "dwright" : dir === "downleft" ? "dwleft" : dir;
        const key = base + frame; // e.g., "right1" or "dwright2"
        if (images[key]) {
          imgRef.current!.src = images[key].src;
        } else if (images["right1"]) {
          imgRef.current!.src = images["right1"].src;
        }
      } else {
        // close to target: snap to it
        wrapperRef.current!.style.left = Math.round(t.x) + "px";
        wrapperRef.current!.style.top = Math.round(t.y) + "px";
        pos.current.x = t.x;
        pos.current.y = t.y;

        if (isReturning) {
          // arrived at home: go to sleep and persist position
          setStateInternal("sleep");
          // setSleepPos(Math.round(t.x), Math.round(t.y));
          setIsReturning(false);
          targetPos.current = null;
        } else {
          // just arrived when following near cursor
          if (images["mati2"]) imgRef.current!.src = images["mati2"].src;
        }
      }
    }

    // run loop uses `intervalRef` so it can be cleared externally if needed
    intervalRef.current = window.setInterval(performStep, 80);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, isFollowing, isReturning]);

  useEffect(() => {
    return () => {
      if (nextStateTimeout.current) window.clearTimeout(nextStateTimeout.current);
      if (imageCycleInterval.current) window.clearInterval(imageCycleInterval.current);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // ensure we stop listening and clear timers when entering sleep state
  useEffect(() => {
    if (state !== "sleep") return;

    // stop following/returning, clear target and interval
    following.current = false;
    setIsFollowing(false);
    setIsReturning(false);
    targetPos.current = null;

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (nextStateTimeout.current) {
      window.clearTimeout(nextStateTimeout.current);
      nextStateTimeout.current = null;
    }
    if (imageCycleInterval.current) {
      window.clearInterval(imageCycleInterval.current);
      imageCycleInterval.current = null;
    }

    // set sleep image
    if (imgRef.current && images["sleep1"]) imgRef.current.src = images["sleep1"].src;
  }, [state]);

  if (!enabled) return null;

  function onClick() {
    // if currently following, stop and go to sleep at this spot
    if (following.current) {
      // stop following, and return to saved sleep position
      following.current = false;
      setIsFollowing(false);
      setIsReturning(true);
      const homeX = sleepX ?? 10;
      const homeY = sleepY ?? (window.innerHeight || 400) - nekoSize - 50;
      targetPos.current = { x: homeX, y: homeY };
      return;
    }

    // otherwise wake and start following
    const current = stateMachine[state];
    if (current && current.click) {
      setStateInternal(current.click);
    } else {
      setStateInternal("awake");
    }
    following.current = true;
    setIsFollowing(true);
  }

  function onContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    // right click toggles enabled off -> hide
    setEnabled(false);
  }

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "fixed",
        left: pos.current.x,
        top: pos.current.y,
        width: nekoSize,
        height: nekoSize,
        zIndex: 9999,
        userSelect: "none",
        pointerEvents: "auto",
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onContextMenu={onContextMenu}
      aria-hidden
    >
      <img
        ref={imgRef}
        src={images["sleep1"].src}
        width={nekoSize}
        height={nekoSize}
        style={{ display: "block" }}
        alt="neko"
      />
    </div>
  );
}
